import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang, localizeEnum } from "@/context/LanguageContext";

export default function CarCard({ car, index = 0, span = "normal", hidePrice = false }) {
  const { lang } = useLang();
  const cover = (car.foto && car.foto[0]) || "https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg";
  const shouldHidePrice = hidePrice || !!car.venduto;
  const isNegotiation = car.prezzo === 0;
  const negotiationText = lang === "en" ? "Price on request" : "Trattativa Riservata";
  const priceText = isNegotiation
    ? negotiationText
    : car.prezzo != null
      ? `€ ${Number(car.prezzo).toLocaleString("it-IT")}`
      : (lang === "en" ? "Price on request" : "Prezzo su richiesta");

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      className={`group ${span === "tall" ? "lg:row-span-2" : ""}`}
      data-testid={`car-card-${car.id}`}
    >
      <Link to={`/vetture/${car.id}`} className="block">
        <div
          className={`car-frame ${span === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"} bg-neutral-900 border border-white/5`}
          onContextMenu={(e) => e.preventDefault()}
        >
          <img
            src={cover}
            alt={`${car.marca} ${car.modello}`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          {car.venduto && (
            <div className="absolute top-4 left-4 label-eyebrow bg-black/70 backdrop-blur-md px-3 py-2 border border-white/10 text-white">
              {lang === "en" ? "Sold" : "Venduta"}
            </div>
          )}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-white text-black p-2 rounded-full">
            <ArrowUpRight size={16} />
          </div>
        </div>
        <div className="mt-6 flex items-end justify-between gap-4">
          <div>
            <div className="label-eyebrow mb-2 text-neutral-500">{car.anno || "—"}</div>
            <h3 className="font-sans font-light text-2xl sm:text-3xl text-white leading-tight">
              {car.marca}
            </h3>
            <p className="font-sans font-light text-neutral-400 text-xl sm:text-2xl leading-tight">
              {car.modello}
            </p>
          </div>
          <div className="text-right">
            {shouldHidePrice ? (
              <>
                <div className="label-eyebrow mb-2 text-neutral-500">{lang === "en" ? "Status" : "Stato"}</div>
                <div className="font-sans font-light text-xl text-white">{lang === "en" ? "Sold" : "Venduta"}</div>
              </>
            ) : (
              <>
                <div className="label-eyebrow mb-2 text-neutral-500">
                  {isNegotiation ? (lang === "en" ? "Availability" : "Disponibilità") : (lang === "en" ? "Price" : "Prezzo")}
                </div>
                <div className={`font-sans text-white leading-tight ${isNegotiation ? "font-medium text-lg sm:text-xl tracking-tight whitespace-nowrap" : "font-light text-xl"}`}>{priceText}</div>
              </>
            )}
          </div>
        </div>
        {(car.km != null || car.cv != null) && (
          <div className="mt-4 flex gap-6 text-sm text-neutral-500">
            {car.km != null && <span>{Number(car.km).toLocaleString("it-IT")} km</span>}
            {car.cv != null && <span>{car.cv} CV</span>}
            {car.alimentazione && <span className="capitalize">{localizeEnum(lang, car.alimentazione)}</span>}
          </div>
        )}
      </Link>
    </motion.div>
  );
}
